import { forwardRef, useCallback } from "react";

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button";
import { Button } from "@/components/tiptap-ui-primitive/button";

// --- Icons ---
import { MathIcon } from "@/components/tiptap-icons/math-icon";

export interface BlockMathButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
}

/**
 * Toolbar button to insert a block math formula.
 * Uses inline editing - click the formula to edit LaTeX directly.
 */
export const BlockMathButton = forwardRef<HTMLButtonElement, BlockMathButtonProps>(
  ({ text, onClick, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor();

    const label = "Insert block formula";

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        
        if (!editor) {
          return;
        }

        try {
          const commands = (editor as any).commands;
          
          if (typeof commands?.insertBlockMath !== "function") {
            console.error("BlockMathButton: insertBlockMath command not available");
            return;
          }

          // Insert block math with default LaTeX - user can click to edit inline
          const result = editor.chain().focus().insertBlockMath({ latex: "\\int_0^1 f(x)\\,dx" }).run();
          
          if (!result) {
            console.error("BlockMathButton: insertBlockMath command returned false");
          }
        } catch (err) {
          console.error("BlockMathButton: insert failed", err);
        }
      },
      [editor, onClick],
    );

    if (!editor) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        <>
          <MathIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      </Button>
    );
  },
);

BlockMathButton.displayName = "BlockMathButton";
